import { useState, useRef, useEffect, useLayoutEffect, useCallback, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Pencil, Plus, Search, Trash2, X } from "lucide-react";

export interface CustomDropdownOption {
  label: string;
  value: string;
  /** Visible but cannot be selected, including by keyboard. */
  disabled?: boolean;
  /** An expandable group. Its own value is not a destination; children are. */
  children?: CustomDropdownOption[];
  /** Renders as a non-clickable section label (like an optgroup heading).
   *  Keyboard navigation and selection skip header rows. */
  header?: boolean;
  /** Inline style applied to the option's label text (e.g. the rich-text
   *  editor's Font Style menu renders each option in its own typeface). */
  labelStyle?: CSSProperties;
}

/** Per-option manage actions (rename/delete) rendered as small icon buttons on
 *  qualifying rows. `canManage` decides which rows get them (e.g. excludes
 *  placeholder rows and locked built-in entries). Rename edits inline within
 *  the open menu; delete closes the menu and hands off to the caller (which
 *  may open a confirm/reclassify dialog). */
export interface CustomDropdownOptionActions {
  canManage: (value: string) => boolean;
  onRename: (value: string, newName: string) => void;
  onDelete: (value: string) => void;
}

interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: CustomDropdownOption[];
  testId?: string;
  className?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  clearSearchLabel?: string;
  emptyMessage?: string;
  disabled?: boolean;
  /** Font-size utility for the trigger + list text. Defaults to "text-[15px]"
   *  (absolute px — the dashboard's 20px root inflates rem-based classes like
   *  text-sm to 17.5px). Pass an override only where a surface needs a
   *  different size (e.g. "text-xs" filter pills, marketing pages). */
  textSizeClass?: string;
  /** Tighter trigger: smaller absolute text + reduced padding, for filter bars
   *  where the control should hug its label (matches dashboard pill density). */
  compact?: boolean;
  /** When provided (with `searchable`), typing a value that matches no existing
   *  option surfaces a "create" row at the bottom of the list. Selecting it calls
   *  this with the trimmed query so the caller can create + add the new entry. */
  onCreateOption?: (query: string) => void;
  /** Label for the create row. Defaults to `Add "<query>"`. */
  createLabel?: (query: string) => string;
  /** Shows a spinner on the create row while the caller persists the new entry. */
  creating?: boolean;
  /** Per-option rename/delete affordances. See CustomDropdownOptionActions. */
  optionActions?: CustomDropdownOptionActions;
  /** Accessible name for the trigger button, composed with the current
   *  selection ("<ariaLabel>: <selected>") so screen readers hear both the
   *  purpose and the value. REQUIRED when the control can render with no
   *  selected option label (an empty trigger is an axe button-name
   *  violation); recommended everywhere for context beyond the bare value. */
  ariaLabel?: string;
  placeholder?: string;
}

export function CustomDropdown({ value, onChange, options, testId, className = "", searchable = false, searchPlaceholder = "Search...", clearSearchLabel = "Clear search", emptyMessage = "No matches found", disabled = false, textSizeClass, compact = false, onCreateOption, createLabel, creating = false, optionActions, ariaLabel, placeholder }: CustomDropdownProps) {
  const resolvedTextSize = textSizeClass ?? "text-[15px]";
  const triggerPadding = compact ? "px-2.5 py-1.5" : "px-3 py-1.5 sm:py-2";
  const [open, setOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");
  // Inline-rename state (only used when optionActions is provided): the value
  // of the row currently being edited, plus the draft text.
  const [renamingValue, setRenamingValue] = useState<string | null>(null);
  const [renameText, setRenameText] = useState("");
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setRenamingValue(null);
      setRenameText("");
    }
  }, [open]);

  useEffect(() => {
    if (renamingValue !== null && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingValue]);

  const commitRename = useCallback((oldValue: string, label: string) => {
    const trimmed = renameText.trim();
    if (trimmed && trimmed !== label) {
      optionActions?.onRename(oldValue, trimmed);
    }
    setRenamingValue(null);
    setRenameText("");
  }, [renameText, optionActions]);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, maxH: 300, openUp: false });
  // Multi-character typeahead buffer (WAI-ARIA listbox pattern, same as
  // SavedViewsMenu): rapid keystrokes accumulate into a search string that
  // jumps the highlight to the next matching option; it resets after a short
  // idle timeout so the next slow keypress starts a fresh search.
  const typeaheadBufferRef = useRef("");
  const typeaheadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTypeahead = useCallback(() => {
    typeaheadBufferRef.current = "";
    if (typeaheadTimerRef.current) {
      clearTimeout(typeaheadTimerRef.current);
      typeaheadTimerRef.current = null;
    }
  }, []);

  // Drop any half-typed typeahead search when the menu closes, and clear the
  // pending reset timer on unmount so it never fires after teardown.
  useEffect(() => {
    if (!open) clearTypeahead();
    return clearTypeahead;
  }, [open, clearTypeahead]);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // The portaled menu root (search header + options list). The outside-click
  // closer must test containment against THIS, not listRef.
  const menuRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      // Check the whole portaled menu root (menuRef), not just the options
      // list (listRef): the searchable variant's search header row sits
      // OUTSIDE listRef, and checking only listRef made a click/tap into the
      // search input count as "outside" and close the menu.
      if (ref.current && !ref.current.contains(e.target as Node) &&
          menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // After the portaled menu renders at its natural (max-content) width, clamp
  // `left` so a menu wider than its trigger never overflows the right edge of
  // the viewport. Depends on `pos` too: the trigger-position effect below
  // rewrites `left` from the trigger rect (unaware of the measured menu
  // width), so the clamp must re-run after every position write. The
  // functional update returns the same object when nothing changes, so this
  // settles in one extra pass instead of looping.
  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    const margin = 8;
    const vw = window.innerWidth;
    const w = menuRef.current.getBoundingClientRect().width;
    setPos(p => {
      let left = p.left;
      if (left + w > vw - margin) left = vw - margin - w;
      if (left < margin) left = margin;
      return left === p.left ? p : { ...p, left };
    });
  }, [open, options, searchQuery, pos]);

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const gap = 4;
      const margin = 8;
      const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
      const spaceAbove = rect.top - gap - margin;
      const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
      const maxH = Math.min(Math.max(openUp ? spaceAbove : spaceBelow, 120), 320);
      const vw = window.innerWidth;
      const maxDropdownW = vw - margin * 2;
      const desiredW = Math.max(rect.width, 160);
      const dropdownW = Math.min(desiredW, maxDropdownW);
      let left = rect.left;
      if (left + dropdownW > vw - margin) {
        left = vw - margin - dropdownW;
      }
      if (left < margin) left = margin;
      setPos({
        top: openUp ? rect.top - gap : rect.bottom + gap,
        left,
        width: dropdownW,
        maxH,
        openUp,
      });
      const idx = filteredOptions.findIndex(o => !o.header && !o.disabled && o.value === value);
      const firstSelectable = filteredOptions.findIndex(o => !o.header && !o.disabled);
      setHighlightIndex(idx >= 0 ? idx : firstSelectable);
    }
  }, [open, options, value, searchable]);

  // Reset the search + focus the input ONLY when the menu transitions to
  // open — NOT in the positioning effect above. That effect re-runs whenever
  // the `options` prop identity changes (parents rebuild the array every
  // render, so ANY parent re-render while the menu is open re-fires it — e.g.
  // a background react-query refetch of the taxonomy list), and having
  // `setSearchQuery("")` there silently wiped a half-typed search query mid-
  // use (surfaced as the e2e-mobile-popups "create row missing" flake under
  // full-suite load, where a refetch landed between keystrokes and the
  // create-row assertion).
  useEffect(() => {
    if (!open) return;
    setSearchQuery("");
    if (searchable) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [open, searchable]);

  useEffect(() => {
    if (open && listRef.current && highlightIndex >= 0) {
      const items = listRef.current.children;
      if (items[highlightIndex]) {
        // Optional call: jsdom (tests) doesn't implement scrollIntoView.
        (items[highlightIndex] as HTMLElement).scrollIntoView?.({ block: "nearest" });
      }
    }
  }, [highlightIndex, open]);

  useEffect(() => {
    if (!open) return;

    const handleWheel = (e: WheelEvent) => {
      const el = listRef.current;
      if (!el) return;
      if (el.contains(e.target as Node)) {
        const { scrollTop, scrollHeight, clientHeight } = el;
        const maxScroll = scrollHeight - clientHeight;
        if (maxScroll <= 0) {
          e.preventDefault();
          return;
        }
        if ((scrollTop <= 0 && e.deltaY < 0) ||
            (scrollTop >= maxScroll && e.deltaY > 0)) {
          e.preventDefault();
        }
      } else {
        e.preventDefault();
      }
    };

    const handleTouch = (e: TouchEvent) => {
      const el = listRef.current;
      if (el && !el.contains(e.target as Node)) {
        e.preventDefault();
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: false });
    document.addEventListener("touchmove", handleTouch, { passive: false });
    return () => {
      document.removeEventListener("wheel", handleWheel);
      document.removeEventListener("touchmove", handleTouch);
    };
  }, [open]);

  const expandedOptions = options.flatMap((option) =>
    option.children && expandedGroup === option.value ? [option, ...option.children] : [option]);
  const filteredOptions = searchable && searchQuery.trim()
    ? expandedOptions.filter(o => !o.header && o.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : expandedOptions;
  const childValues = new Set(options.flatMap((option) => option.children?.map((child) => child.value) ?? []));
  const openMenu = () => {
    setExpandedGroup(options.find((option) => option.children?.some((child) => child.value === value))?.value ?? null);
    setOpen(true);
  };
  const activateOption = (option: CustomDropdownOption) => {
    if (option.disabled) return;
    if (option.children) {
      setExpandedGroup((previous) => previous === option.value ? null : option.value);
      return;
    }
    onChange(option.value);
    setOpen(false);
  };

  const stepHighlight = useCallback((from: number, dir: 1 | -1) => {
    const n = filteredOptions.length;
    if (n === 0) return -1;
    let next = from;
    for (let step = 0; step < n; step++) {
      next = (next + dir + n) % n;
      if (!filteredOptions[next]?.header && !filteredOptions[next]?.disabled) return next;
    }
    return from;
  }, [filteredOptions]);

  const trimmedQuery = searchQuery.trim();
  const showCreateRow =
    !!onCreateOption &&
    searchable &&
    trimmedQuery.length > 0 &&
    !options.some(o => o.label.toLowerCase() === trimmedQuery.toLowerCase());

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openMenu();
        return;
      }
      return;
    }

    // Multi-character typeahead: printable keys accumulate into a search
    // buffer and jump the highlight to the next option whose label starts
    // with it ("br" → "Branding"); same-letter repeats cycle the matches.
    // Suspended while any inline text input inside the dropdown owns the
    // keystroke (the searchable variant's search box types/filters instead;
    // the managed-taxonomy rename input stops propagation before reaching
    // here, but the guard covers any future inline field too).
    const isTypeaheadKey =
      e.key.length === 1 &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey &&
      /\S/.test(e.key);
    const targetIsTextInput =
      e.target instanceof HTMLElement &&
      (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA");
    if (isTypeaheadKey && !targetIsTextInput) {
      e.preventDefault();
      const char = e.key.toLowerCase();
      typeaheadBufferRef.current += char;
      if (typeaheadTimerRef.current) clearTimeout(typeaheadTimerRef.current);
      typeaheadTimerRef.current = setTimeout(() => {
        typeaheadBufferRef.current = "";
        typeaheadTimerRef.current = null;
      }, 500);
      const buffer = typeaheadBufferRef.current;
      // An all-same-letter buffer behaves like single-letter cycling
      // (pressing "b" repeatedly keeps cycling the "b" options).
      const isRepeatedChar =
        buffer.length > 1 && buffer.split("").every(c => c === char);
      const search = isRepeatedChar ? char : buffer;
      // Single letters (and same-letter repeats) cycle starting AFTER the
      // current option; a growing multi-letter search starts AT the current
      // option so it can stay put while the match narrows.
      const startStep = isRepeatedChar || buffer.length === 1 ? 1 : 0;
      const n = filteredOptions.length;
      const from = highlightIndex < 0 ? 0 : highlightIndex;
      for (let step = startStep; step < startStep + n; step++) {
        const idx = (from + step + n) % n;
        const opt = filteredOptions[idx];
        if (opt.header || opt.disabled) continue;
        if (opt.label.trim().toLowerCase().startsWith(search)) {
          setHighlightIndex(idx);
          return;
        }
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        // Explicit navigation abandons any in-progress typeahead search.
        clearTypeahead();
        setHighlightIndex(prev => stepHighlight(prev, 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        clearTypeahead();
        setHighlightIndex(prev => stepHighlight(prev, -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < filteredOptions.length && !filteredOptions[highlightIndex].header && !filteredOptions[highlightIndex].disabled) {
          activateOption(filteredOptions[highlightIndex]);
        }
        break;
      case " ":
        if (!searchable) {
          e.preventDefault();
          if (highlightIndex >= 0 && highlightIndex < filteredOptions.length && !filteredOptions[highlightIndex].header && !filteredOptions[highlightIndex].disabled) {
            activateOption(filteredOptions[highlightIndex]);
          }
        }
        break;
      case "ArrowRight":
        if (filteredOptions[highlightIndex]?.children) {
          e.preventDefault();
          setExpandedGroup(filteredOptions[highlightIndex].value);
        }
        break;
      case "ArrowLeft":
        if (childValues.has(filteredOptions[highlightIndex]?.value ?? "")) {
          const parent = options.find((option) =>
            option.value === expandedGroup && option.children?.some((child) => child.value === filteredOptions[highlightIndex].value));
          if (parent) {
            e.preventDefault();
            setExpandedGroup(null);
            setHighlightIndex(options.indexOf(parent));
          }
        }
        break;
      case "Escape":
        // Close only the dropdown — stop the event so a parent dialog's
        // Escape handler doesn't also close the dialog underneath.
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        break;
      case "Tab":
        setOpen(false);
        break;
    }
  }, [open, highlightIndex, filteredOptions, onChange, searchable, stepHighlight, clearTypeahead, options, expandedGroup]);

  useEffect(() => {
    if (searchable && searchQuery !== undefined) {
      setHighlightIndex(filteredOptions.findIndex(o => !o.header && !o.disabled));
    }
  }, [searchQuery, searchable]);

  const parentOfSelection = options.find((option) => option.children?.some((child) => child.value === value));
  const selectedOption = parentOfSelection?.children?.find((child) => child.value === value)
    ?? options.find((option) => !option.header && option.value === value);
  const selectedLabel = selectedOption
    ? parentOfSelection ? `${parentOfSelection.label} — ${selectedOption.label}` : selectedOption.label
    : value || placeholder || "";
  // Simple select-only dropdowns can expose the complete combobox/listbox
  // pattern. Search/create and managed taxonomy variants contain additional
  // interactive controls, so they retain their existing menu-button behavior
  // rather than placing nested controls inside ARIA options.
  const hasGroups = options.some((option) => option.children);
  const useListboxSemantics =
    !!ariaLabel && !searchable && !onCreateOption && !optionActions
    && !hasGroups;
  const listboxId = `${testId ?? "dropdown"}-listbox`;
  const optionId = (optionValue: string) =>
    `${listboxId}-option-${encodeURIComponent(optionValue || "empty")}`;
  // Compose the accessible name so screen readers hear both the control's
  // purpose and its current value ("Source: Referral"). Without ariaLabel the
  // trigger's own text content (selectedLabel) remains the name, as before.
  const triggerAriaLabel = ariaLabel
    ? (selectedLabel ? `${ariaLabel}: ${selectedLabel}` : ariaLabel)
    : undefined;

  const dropdownList = open ? createPortal(
    <div
      ref={menuRef}
      data-testid={`${testId ?? "dropdown"}-menu`}
      onKeyDown={handleKeyDown}
      role={hasGroups ? "menu" : undefined}
      style={{
        position: "fixed",
        // Modal dialogs set pointer-events:none on body. This body portal
        // remains in the dialog's React event tree, but must explicitly
        // restore pointer input instead of inheriting the body's lock.
        pointerEvents: "auto",
        top: pos.openUp ? undefined : pos.top,
        bottom: pos.openUp ? (window.innerHeight - pos.top) : undefined,
        left: pos.left,
        // Size to the widest option label (width: max-content) so long entries
        // aren't truncated, but never narrower than the trigger and never wider
        // than the viewport. The layout effect below re-clamps `left` once the
        // real width is known.
        minWidth: pos.width,
        width: "max-content",
        maxWidth: window.innerWidth - 16,
        maxHeight: pos.maxH,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
      }}
      className="bg-card border border-border rounded-md shadow-xl"
    >
      {searchable && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <Search size={14} strokeWidth={1.5} className="text-foreground shrink-0" />
          <input
            ref={searchInputRef}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            className={`flex-1 min-w-0 bg-transparent text-foreground ${resolvedTextSize} outline-none placeholder:text-foreground/60`}
            data-testid={`${testId ?? "dropdown"}-search`}
          />
          {searchQuery && (
            <button
              type="button"
              aria-label={clearSearchLabel}
              onClick={() => setSearchQuery("")}
              className="text-foreground hover:text-foreground cursor-pointer transition-colors"
            >
              <X size={14} strokeWidth={1.5} />
            </button>
          )}
        </div>
      )}
      <div
        ref={listRef}
        id={listboxId}
        role={useListboxSemantics ? "listbox" : undefined}
        aria-label={useListboxSemantics ? ariaLabel : undefined}
        style={{ overflowY: "auto", flex: 1 }}
      >
        {filteredOptions.length === 0 && !showCreateRow ? (
          <div className={`px-3 py-3 ${resolvedTextSize} text-foreground text-center`}>{emptyMessage}</div>
        ) : (
          filteredOptions.map((opt, i) => {
            if (opt.header) {
              return (
                <div
                  key={`__header-${opt.label}-${i}`}
                   role={useListboxSemantics ? "presentation" : undefined}
                  className="px-3 pt-2.5 pb-1 text-[12px] font-semibold uppercase tracking-wide text-foreground/50 select-none"
                >
                  {opt.label}
                </div>
              );
            }
            const isSelected = value === opt.value;
            const isHighlighted = i === highlightIndex;
            if (opt.children) {
              return (
                <button
                  type="button"
                  key={opt.value}
                  role="menuitem"
                  aria-expanded={expandedGroup === opt.value}
                  data-testid={`${testId ?? "dropdown"}-group-${opt.value.slice("__group__".length)}`}
                  onClick={() => { setHighlightIndex(i); activateOption(opt); }}
                  onMouseEnter={() => setHighlightIndex(i)}
                  className={`w-full text-left px-3 py-2 ${resolvedTextSize} flex items-center justify-start gap-1.5 hover:bg-muted ${isHighlighted ? "bg-muted" : ""}`}
                >
                  <span className="min-w-0 truncate">{opt.label}</span>
                  <ChevronDown size={14} className={`shrink-0 transition-transform ${expandedGroup === opt.value ? "rotate-180" : ""}`} />
                </button>
              );
            }
            const managed = !!optionActions && optionActions.canManage(opt.value);
            if (managed && renamingValue === opt.value) {
              return (
                <div key={opt.value} className={`pl-3 pr-2 py-1.5 flex items-center gap-1.5 ${resolvedTextSize}`}>
                  <input
                    ref={renameInputRef}
                    data-testid={`${testId ?? "dropdown"}-rename-input`}
                    aria-label={`New name for ${opt.label}`}
                    type="text"
                    value={renameText}
                    onChange={e => setRenameText(e.target.value)}
                    onKeyDown={e => {
                      e.stopPropagation();
                      if (e.key === "Enter") { e.preventDefault(); commitRename(opt.value, opt.label); }
                      if (e.key === "Escape") { e.preventDefault(); setRenamingValue(null); setRenameText(""); }
                    }}
                    className={`flex-1 min-w-0 bg-card border border-foreground/40 rounded px-2 py-1 ${resolvedTextSize} text-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20`}
                  />
                  <button
                    type="button"
                    data-testid={`${testId ?? "dropdown"}-rename-confirm`}
                    aria-label={`Save new name for ${opt.label}`}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => commitRename(opt.value, opt.label)}
                    className="text-brand-sage hover:text-[#6b7c60] cursor-pointer p-1 shrink-0"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    type="button"
                    data-testid={`${testId ?? "dropdown"}-rename-cancel`}
                    aria-label={`Cancel renaming ${opt.label}`}
                    onMouseDown={e => { e.preventDefault(); setRenamingValue(null); setRenameText(""); }}
                    className="text-foreground hover:text-rose-700 cursor-pointer p-1 shrink-0"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            }
            if (managed) {
              return (
                <div
                  key={opt.value}
                  onMouseEnter={() => setHighlightIndex(i)}
                  className={`w-full flex items-center gap-0.5 pr-1.5 group/opt transition-colors ${
                    isHighlighted
                      ? "bg-muted text-foreground"
                      : isSelected
                      ? "bg-background text-foreground"
                      : "text-foreground hover:bg-background"
                  }`}
                >
                  <button
                    type="button"
                    data-testid={`${testId ?? "dropdown"}-option-${opt.value}`}
                    onClick={() => { onChange(opt.value); setOpen(false); }}
                    className={`flex-1 min-w-0 text-left pl-3 pr-1 py-2 ${resolvedTextSize} cursor-pointer truncate flex items-center justify-between gap-2 ${isSelected ? "font-medium" : "font-normal"}`}
                  >
                    <span className="truncate">{opt.label}</span>
                    {isSelected && <span className="text-foreground shrink-0">✓</span>}
                  </button>
                  <button
                    type="button"
                    data-testid={`${testId ?? "dropdown"}-rename-${opt.value}`}
                    aria-label={`Rename ${opt.label}`}
                    title="Rename"
                    onClick={() => { setRenamingValue(opt.value); setRenameText(opt.label); }}
                    className="shrink-0 p-1.5 rounded text-foreground/40 hover:text-foreground cursor-pointer transition-colors"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    data-testid={`${testId ?? "dropdown"}-delete-${opt.value}`}
                    aria-label={`Delete ${opt.label}`}
                    title="Delete"
                    onClick={() => { setOpen(false); optionActions!.onDelete(opt.value); }}
                    className="shrink-0 p-1.5 rounded text-foreground/40 hover:text-rose-700 cursor-pointer transition-colors"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            }
            return (
              <button
                type="button"
                key={opt.value}
                id={useListboxSemantics ? optionId(opt.value) : undefined}
                  role={hasGroups ? "menuitem" : useListboxSemantics ? "option" : undefined}
                  aria-label={childValues.has(opt.value) ? `${options.find((group) => group.children?.some((child) => child.value === opt.value))?.label}: ${opt.label}` : undefined}
                aria-selected={useListboxSemantics ? isSelected : undefined}
                aria-disabled={opt.disabled || undefined}
                disabled={opt.disabled}
                data-testid={`${testId ?? "dropdown"}-option-${opt.value}`}
                  onClick={() => activateOption(opt)}
                onMouseEnter={() => { if (!opt.disabled) setHighlightIndex(i); }}
                  className={`w-full text-left ${childValues.has(opt.value) ? "pl-7" : "pl-3"} pr-4 py-2 ${resolvedTextSize} cursor-pointer transition-colors truncate flex items-center justify-between gap-2 ${
                  isHighlighted
                    ? "bg-muted text-foreground"
                    : isSelected
                    ? "bg-background text-foreground"
                    : "text-foreground hover:bg-background"
                } ${isSelected ? "font-medium" : "font-normal"} ${opt.disabled ? "cursor-not-allowed opacity-50" : ""}`}
              >
                <span className="truncate" style={opt.labelStyle}>{opt.label}</span>
                {isSelected && <span className="text-foreground shrink-0">✓</span>}
              </button>
            );
          })
        )}
        {showCreateRow && (
          <button
            type="button"
            onClick={() => { if (!creating) { onCreateOption!(trimmedQuery); } }}
            disabled={creating}
            data-testid={`${testId ?? "dropdown"}-create`}
            className={`w-full text-left pl-3 pr-4 py-2 ${resolvedTextSize} cursor-pointer transition-colors truncate flex items-center gap-2 border-t border-border font-medium text-foreground hover:bg-background disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            <Plus size={14} strokeWidth={2} className="shrink-0" />
            <span className="truncate">
              {(createLabel ?? ((q: string) => `Add "${q}"`))(trimmedQuery)}
            </span>
          </button>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative" ref={ref}>
      <button
        ref={btnRef}
        type="button"
        data-testid={testId}
        role={useListboxSemantics ? "combobox" : undefined}
        aria-label={triggerAriaLabel}
        aria-haspopup={hasGroups ? "menu" : "listbox"}
        aria-expanded={open}
        aria-controls={useListboxSemantics && open ? listboxId : undefined}
        aria-activedescendant={
          useListboxSemantics && open && highlightIndex >= 0 && filteredOptions[highlightIndex] && !filteredOptions[highlightIndex].header && !filteredOptions[highlightIndex].disabled
            ? optionId(filteredOptions[highlightIndex].value)
            : undefined
        }
        disabled={disabled}
        onClick={() => { if (!disabled) { if (open) setOpen(false); else openMenu(); } }}
        onKeyDown={handleKeyDown}
        className={`w-full bg-card border border-border rounded-md ${triggerPadding} ${resolvedTextSize} text-foreground focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20 flex items-center justify-between transition-colors ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-border cursor-pointer"} ${className}`}
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown size={14} strokeWidth={1.5} className={`text-foreground transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {dropdownList}
    </div>
  );
}
